// ─────────────────────────────────────────────────────────────────────────────
// V2Notifications — messages between Super Admin and entities (enterprise accounts).
// Used at /dashboard/notifications (entity users) and /superadmin/notifications (super admin).
// Endpoints (v1's, plus the two compose endpoints the server already has):
//   GET  /api/notifications            inbox (entity: to all/this entity; super admin: entity queries)
//   GET  /api/notifications/sent       super admin's sent messages (merged with the inbox, like v1)
//   POST /api/notifications/:id/read | /acknowledge (entity) | /resolve (super admin) | /reply { message }
//   POST /api/notifications/send  { toType: "all"|"entity", toId, subject, message, type, priority }   (super admin)
//   POST /api/notifications/query { subject, message, priority }                                      (entity)
// Fixes vs v1: Acknowledge appears (v1 checked a non-existent "sent" status); filters are applied
// client-side (the server ignores priority/status params).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { Segmented, Sheet, Skel, useMedia } from "./primitives";
import { Field, PageHead } from "./kit";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const json = () => ({ "Content-Type": "application/json", ...auth() });
const TYPE = { announcement: "Announcement", alert: "Alert", task: "Task", query: "Query", report: "Report" };
const PRIO = { normal: ["Normal", "neutral"], high: ["High", "warn"], urgent: ["Urgent", "bad"] };
const STAT = { unread: ["New", "info"], read: ["Read", "neutral"], acknowledged: ["Acknowledged", "good"], resolved: ["Resolved", "good"] };
const when = (d) => {
  if (!d) return "";
  const dt = new Date(d), m = Math.floor((Date.now() - dt) / 60000);
  if (m < 1) return "Just now"; if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: dt.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
};
const full = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export default function V2Notifications({ showAlert }) {
  usePageTitle("Messages");
  const { user } = useContext(userContext);
  const isSA = user?.role === "superadmin";
  const isNarrow = useMedia("(max-width: 900px)");
  const [list, setList] = useState(null);
  const [selId, setSelId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [prio, setPrio] = useState("all");
  const [q, setQ] = useState("");
  const [compose, setCompose] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState("");
  const threadEnd = useRef(null);

  const load = useCallback(async () => {
    try {
      const get = async (u) => { const r = await fetch(`${API_BASE}${u}`, { headers: auth() }); return r.ok ? (await r.json()).notifications || [] : []; };
      const parts = await Promise.all(isSA ? [get("/api/notifications/sent?limit=100"), get("/api/notifications?limit=100")] : [get("/api/notifications?limit=100")]);
      const merged = [...new Map(parts.flat().map((n) => [n._id, n])).values()].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
      setList(merged);
    } catch { setList((l) => l || []); }
  }, [isSA]);
  useEffect(() => { if (user) load(); }, [load, user]);
  useEffect(() => { const t = setInterval(() => { if (!document.hidden) load(); }, 30000); return () => clearInterval(t); }, [load]);

  const mine = (n) => (isSA ? n.fromType === "superadmin" : n.fromType === "entity");
  const unread = (n) => !mine(n) && !n.isRead;
  const all = list || [];
  const counts = { all: all.length, unread: all.filter(unread).length, open: all.filter((n) => !["acknowledged", "resolved"].includes(n.status)).length };
  const shown = useMemo(() => all.filter((n) =>
    (filter === "all" || (filter === "unread" && unread(n)) || (filter === "open" && !["acknowledged", "resolved"].includes(n.status)) || (filter === "resolved" && n.status === "resolved"))
    && (prio === "all" || n.priority === prio)
    && (!q.trim() || `${n.subject} ${n.message} ${n.fromName} ${n.fromEntityName} ${n.toName}`.toLowerCase().includes(q.trim().toLowerCase()))), [all, filter, prio, q]); // eslint-disable-line
  const sel = all.find((n) => n._id === selId) || null;
  useEffect(() => { if (!isNarrow && !selId && shown.length) setSelId(shown[0]._id); }, [isNarrow, shown, selId]);

  // Opening an unread message marks it read (same endpoint as v1)
  useEffect(() => {
    if (!sel || !unread(sel)) return;
    fetch(`${API_BASE}/api/notifications/${sel._id}/read`, { method: "POST", headers: auth() }).catch(() => {});
    setList((l) => l.map((n) => (n._id === sel._id ? { ...n, isRead: true, status: n.status === "unread" ? "read" : n.status } : n)));
  }, [selId]); // eslint-disable-line
  useEffect(() => { setReply(""); threadEnd.current?.scrollIntoView({ block: "end" }); }, [selId, sel?.thread?.length]);

  const act = async (kind) => {
    setBusy(kind);
    try {
      const r = await fetch(`${API_BASE}/api/notifications/${sel._id}/${kind}`, { method: "POST", headers: kind === "reply" ? json() : auth(), body: kind === "reply" ? JSON.stringify({ message: reply.trim() }) : undefined });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't update the message");
      if (kind === "reply") setReply("");
      showAlert?.(kind === "reply" ? "Reply sent" : kind === "resolve" ? "Marked resolved" : "Acknowledged", "success");
      await load();
    } catch (e) { showAlert?.(e.message, "danger"); } finally { setBusy(""); }
  };

  const row = (n) => {
    const [pl, pt] = PRIO[n.priority] || PRIO.normal;
    return (
      <button key={n._id} type="button" className={`v2c-row v2nt-row${n._id === selId && !isNarrow ? " is-sel" : ""}${unread(n) ? " is-unread" : ""}`} onClick={() => setSelId(n._id)}>
        <span className="v2nt-dot" aria-hidden="true" />
        <span className="v2-row-main">
          <span className="v2nt-row-top"><span className="v2-row-title">{n.subject}</span><time>{when(n.updatedAt || n.createdAt)}</time></span>
          <span className="v2-row-sub">{mine(n) ? `To ${n.toName || (n.toType === "all" ? "All entities" : "Super Admin")}` : `From ${n.fromEntityName || n.fromName || "Super Admin"}`} · {TYPE[n.type] || "Message"}</span>
          <span className="v2nt-preview">{n.thread?.length ? n.thread[n.thread.length - 1].message : n.message}</span>
        </span>
        {n.priority !== "normal" && <span className={`v2-status v2-status--${pt}`}>{pl}</span>}
      </button>);
  };

  const detail = sel && (() => {
    const [sl, st] = STAT[sel.status] || STAT.read; const [pl, pt] = PRIO[sel.priority] || PRIO.normal;
    const canAck = !isSA && !mine(sel) && ["unread", "read"].includes(sel.status);
    const canResolve = isSA && sel.status !== "resolved";
    const bubbles = [{ fromType: sel.fromType, fromName: sel.fromEntityName || sel.fromName, message: sel.message, timestamp: sel.createdAt }, ...(sel.thread || [])];
    return (
      <div className="v2nt-detail" key={sel._id}>
        <header className="v2nt-head">
          <div className="v2nt-pills">
            <span className="v2-status v2-status--info">{TYPE[sel.type] || "Message"}</span>
            {sel.priority !== "normal" && <span className={`v2-status v2-status--${pt}`}>{pl} priority</span>}
            <span className={`v2-status v2-status--${st}`}>{sl}</span>
          </div>
          <h2>{sel.subject}</h2>
          <span className="v2-row-sub">{mine(sel) ? `You → ${sel.toName || (sel.toType === "all" ? "All entities" : "Super Admin")}` : `${sel.fromEntityName || sel.fromName} → you`} · {full(sel.createdAt)}</span>
        </header>
        <ol className="v2nt-thread">
          {bubbles.map((b, i) => {
            const me = isSA ? b.fromType === "superadmin" : b.fromType === "entity";
            return (
              <li key={i} className={me ? "is-me" : ""}>
                <span className="v2nt-who">{me ? "You" : b.fromName || (b.fromType === "superadmin" ? "Super Admin" : "Entity")}</span>
                <div className="v2nt-bubble">{b.message}</div>
                <time>{full(b.timestamp)}</time>
              </li>);
          })}
          <li ref={threadEnd} aria-hidden="true" className="v2nt-end" />
        </ol>
        {sel.status !== "resolved" ? (
          <div className="v2nt-compose">
            <textarea className="v2-input" rows={2} value={reply} placeholder="Write a reply…" aria-label="Reply"
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && reply.trim()) { e.preventDefault(); act("reply"); } }} />
            <div className="v2nt-compose-bar">
              {canAck && <button type="button" className="v2-btn v2-btn--sm" disabled={!!busy} onClick={() => act("acknowledge")}><Glyph name="check" size={14} stroke={2.2} /> Acknowledge</button>}
              {canResolve && <button type="button" className="v2-btn v2-btn--sm" disabled={!!busy} onClick={() => act("resolve")}><Glyph name="check" size={14} stroke={2.2} /> Mark resolved</button>}
              <span className="v2nt-hint">Ctrl + Enter to send</span>
              <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" disabled={!reply.trim() || !!busy} onClick={() => act("reply")}>{busy === "reply" ? "Sending…" : "Send reply"}</button>
            </div>
          </div>
        ) : <p className="v2nt-closed"><Glyph name="check" size={15} stroke={2.2} /> Resolved {sel.resolvedAt ? when(sel.resolvedAt) : ""} — the conversation is closed.</p>}
      </div>
    );
  })();

  const detailOnly = isNarrow && sel;
  return (
    <div className="v2p v2l v2nt">
      {!detailOnly && <PageHead eyebrow={isSA ? "Super admin" : "Account"} title="Messages"
        sub={isSA ? "Announcements and tasks you send to entities, and their questions to you." : "Announcements, tasks and replies from your Super Admin."}
        actions={<button type="button" className="v2-btn v2-btn--primary" onClick={() => setCompose(true)}><Glyph name="plus" size={17} stroke={2.2} /> {isSA ? "New message" : "Ask Super Admin"}</button>} />}
      {detailOnly && <button type="button" className="v2l-back" onClick={() => setSelId(null)}><Glyph name="chevronLeft" size={18} stroke={2} /> Messages</button>}
      <div className={`v2c-split${detailOnly ? " is-detail" : ""}`}>
        {!detailOnly && (
          <section className="v2-card v2c-listcard v2nt-list">
            <div className="v2-field--search v2c-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search messages" aria-label="Search messages" /></div>
            <Segmented size="sm" ariaLabel="Filter" value={filter} onChange={setFilter} options={[
              { value: "all", label: "All" }, { value: "unread", label: `Unread${counts.unread ? ` · ${counts.unread}` : ""}` }, { value: "open", label: "Open" }, { value: "resolved", label: "Resolved" }]} />
            <div className="v2nt-prio">{[["all", "Any priority"], ["urgent", "Urgent"], ["high", "High"]].map(([k, l]) => (
              <button key={k} type="button" className={`v2-chip-btn v2i-preset${prio === k ? " is-on" : ""}`} onClick={() => setPrio(k)}>{l}</button>))}</div>
            {!list ? <div className="v2-list">{[0, 1, 2, 3].map((i) => <Skel key={i} h={72} r={16} />)}</div>
              : !shown.length ? <div className="v2c-empty"><Duo name="mail" size={44} /><strong>{all.length ? "Nothing here" : "No messages yet"}</strong><span>{all.length ? "Try another filter." : isSA ? "Send an announcement or task to your entities." : "Messages from your Super Admin will appear here."}</span></div>
              : <div className="v2c-list">{shown.map(row)}</div>}
          </section>
        )}
        {(!isNarrow || sel) && <section className={isNarrow ? "" : "v2-card v2c-detailcard"}>{detail || <div className="v2c-empty"><Duo name="mail" size={44} /><span>Choose a message.</span></div>}</section>}
      </div>
      <ComposeSheet open={compose} isSA={isSA} onClose={() => setCompose(false)} onSent={(m) => { setCompose(false); showAlert?.(m, "success"); load(); }} />
    </div>
  );
}

function ComposeSheet({ open, isSA, onClose, onSent }) {
  const [f, setF] = useState({ to: "all", toId: "", subject: "", message: "", type: "announcement", priority: "normal" });
  const [entities, setEntities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open) return;
    setF({ to: "all", toId: "", subject: "", message: "", type: "announcement", priority: "normal" }); setErr("");
    if (isSA) fetch(`${API_BASE}/api/superadmin/entities`, { headers: auth() }).then((r) => r.json()).then((d) => setEntities(Array.isArray(d) ? d.filter((e) => e.isActive !== false) : [])).catch(() => setEntities([]));
  }, [open, isSA]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const send = async () => {
    setErr("");
    if (!f.subject.trim() || !f.message.trim()) { setErr("Add a subject and a message."); return; }
    if (isSA && f.to === "entity" && !f.toId) { setErr("Choose an entity."); return; }
    setBusy(true);
    try {
      const body = isSA ? { toType: f.to, toId: f.to === "entity" ? f.toId : undefined, subject: f.subject.trim(), message: f.message.trim(), type: f.type, priority: f.priority }
        : { subject: f.subject.trim(), message: f.message.trim(), priority: f.priority };
      const r = await fetch(`${API_BASE}/api/notifications/${isSA ? "send" : "query"}`, { method: "POST", headers: json(), body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't send");
      onSent(d.message || "Message sent");
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} width={560} title={isSA ? "New message" : "Ask Super Admin"} subtitle={isSA ? "Goes to the entity's Messages page" : "Your Super Admin will reply here"}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button><button type="button" className="v2-btn v2-btn--primary" disabled={busy} onClick={send}>{busy ? "Sending…" : "Send"}</button></>}>
      <div className="v2-form">
        {isSA && <Field label="To"><Segmented ariaLabel="Recipients" value={f.to} onChange={set("to")} options={[{ value: "all", label: "All entities" }, { value: "entity", label: "One entity" }]} /></Field>}
        {isSA && f.to === "entity" && <Field label="Entity">
          <select className="v2-input" value={f.toId} onChange={set("toId")}><option value="">Choose an entity…</option>{entities.map((e) => <option key={e._id} value={e._id}>{e.companyName}</option>)}</select></Field>}
        <Field label="Subject"><input className="v2-input" value={f.subject} onChange={set("subject")} autoFocus maxLength={140} /></Field>
        <Field label="Message"><textarea className="v2-input v2nt-textarea" rows={5} value={f.message} onChange={set("message")} /></Field>
        <div className="v2-form-row">
          {isSA && <Field label="Type"><select className="v2-input" value={f.type} onChange={set("type")}>{["announcement", "alert", "task", "report"].map((t) => <option key={t} value={t}>{TYPE[t]}</option>)}</select></Field>}
          <Field label="Priority"><Segmented ariaLabel="Priority" value={f.priority} onChange={set("priority")} options={[{ value: "normal", label: "Normal" }, { value: "high", label: "High" }, { value: "urgent", label: "Urgent" }]} /></Field>
        </div>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </div>
    </Sheet>
  );
}
